import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { mdiDownload, mdiRefresh, mdiStopCircleOutline } from '@mdi/js'
import { $api } from '../../api/queryClient'
import type { AgentLlmCall, AgentMission, AgentRun, AgentRunEvent } from '../../api/schema'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useAsset } from '../../hooks/useQueries'
import { useCan } from '../../contexts/PermissionsContext'
import { useLocale } from '../../contexts/LocaleContext'
import { formatAICost, formatTokens } from '../../lib/aiUsage'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import { EmptyState } from '../ui/empty-state'
import { ActivityEntry, ActivityUserName } from '../activity/ActivityEntry'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import {
    downloadAgentRunTranscript,
    isActiveRun,
    launchAgentRun,
    runAction,
    useAgentMissions,
    useAgentRun,
    useAgentRunLive,
    useInvalidateAgentRuns,
    type TargetEntityType,
} from './agent-runs-api'
import { AGENT_RUN_STATUS, displayStatus, toneVariant } from './agent-run-status'
import { AgentRunCard, pairLabel } from './AgentRunCard'
import { AgentRunMission } from './AgentRunMission'
import { AgentRunProposals } from './AgentRunProposals'
import { useFloorNames } from './AgentRunsPanel'
import { launchErrorMessage } from './LaunchAgentRunDialog'

export const AGENT_RUN_TABS = ['summary', 'events', 'api-calls', 'writes', 'llm-calls'] as const
export type AgentRunTab = (typeof AGENT_RUN_TABS)[number]

const EVENT_CHIPS = ['all', 'step', 'message', 'tool', 'question', 'api_call', 'server'] as const
type EventChip = (typeof EVENT_CHIPS)[number]
const EVENTS_PAGE = 200
const LLM_PAGE = 100

const RUN_PATH = '/api/v1/organizations/{orgCode}/agent-runs/{agentRunUid}'

export function formatDuration(ms: number): string {
    const s = Math.max(0, Math.round(ms / 1000))
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    if (h > 0) return `${h} h ${m} min`
    if (m > 0) return `${m} min ${s % 60} s`
    return `${s} s`
}

export function useTargetName(org: string, run: AgentRun | undefined): { name?: string; path?: string } {
    const { t } = useTranslation('agent-runs')
    const type = run?.target_entity_type as TargetEntityType | undefined
    const uid = run?.target_entity_uid ?? ''
    const { data: asset } = useAsset(org, type === 'asset' ? uid : '')
    const { data: imp } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/imports/{importUid}',
        { params: { path: { orgCode: org, importUid: uid } } },
        { enabled: type === 'import' && !!uid },
    )
    if (type === 'asset') return { name: (asset as { name?: string } | undefined)?.name, path: `/${org}/assets/${uid}` }
    if (type === 'import') return { name: (imp as { label?: string } | undefined)?.label, path: `/${org}/imports/${uid}` }
    // Owners and admins see an assistant run, never its thread: no link
    // (spec 2026-10-21-24 §6).
    if (run?.target_entity_type === 'assistant_thread') {
        return { name: t('target_assistant', { person: run.requested_by?.name ?? '' }) }
    }
    return {}
}

function Fact({ label, children, testid }: { label: string; children: React.ReactNode; testid?: string }) {
    return (
        <div className="grid grid-cols-[10rem_1fr] gap-2 py-1 text-sm" data-testid={testid}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="min-w-0 break-words text-foreground">{children}</dd>
        </div>
    )
}

function Hash({ value }: { value?: string | null }) {
    if (!value) return <span className="text-muted-foreground">—</span>
    return <code className="font-mono text-xs" title={value}>{value.slice(0, 8)}</code>
}

function Meter({ name, value, max }: { name: string; value: number; max: number }) {
    const { t } = useTranslation('agent-runs')
    const ratio = max > 0 ? Math.min(1, value / max) : 0
    return (
        <div className="space-y-1" data-testid={`agent-run-quota-${name}`}>
            <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t(`page.quota.${name}`)}</span>
                <span className="tabular-nums">{value} / {max}</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted">
                <div className={`h-1.5 rounded-full ${ratio >= 0.9 ? 'bg-warning' : 'bg-primary'}`} style={{ width: `${ratio * 100}%` }} />
            </div>
        </div>
    )
}

function SummaryTab({ org, run, mission }: { org: string; run: AgentRun; mission?: AgentMission }) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const canDecide = useCan('data.write')
    const invalidate = useInvalidateAgentRuns(org)
    const floorNames = useFloorNames(org, run)
    const internals = run.internals
    const date = (s?: string | null) => (s ? new Date(s).toLocaleString(locale) : '—')
    const counts = (run.output as { counts?: { writes?: Record<string, Record<string, number>> } } | null)?.counts
    const limited = run.error === 'subscription_limit'
    const { data: llm } = $api.useQuery(
        'get',
        `${RUN_PATH}/llm-calls`,
        { params: { path: { orgCode: org, agentRunUid: run.uid }, query: { limit: 500 } } },
        { enabled: limited },
    )
    const resetAt = limited
        ? [...(llm?.data ?? [])].reverse().find((c) => c.status === 'limited' && c.limit_reset_at)?.limit_reset_at
        : undefined
    const params = Object.entries(run.params ?? {})

    return (
        <div className="space-y-6">
            <section className="rounded-lg border border-border bg-card p-4">
                <AgentRunCard
                    org={org}
                    run={run}
                    mission={mission}
                    canDecide={canDecide}
                    canOpenDetails={false}
                    bodyOnly
                    floorAssetUid={run.target_entity_type === 'asset' ? run.target_entity_uid ?? undefined : undefined}
                    floorNames={floorNames}
                    onDecision={async (d) => {
                        const err = await runAction(org, run.uid, d)
                        await invalidate()
                        return err ? err.title || t('card.decision_failed') : undefined
                    }}
                />
                {resetAt && (
                    <p className="mt-3 text-sm text-muted-foreground" data-testid="agent-run-limit-reset">
                        {t('page.limit_reset', { date: date(resetAt) })}
                    </p>
                )}
            </section>

            <AgentRunProposals org={org} run={run} canDecide={canDecide} />

            <section className="rounded-lg border border-border bg-card p-4" data-testid="agent-run-facts">
                <h2 className="mb-2 text-base font-semibold text-foreground">{t('page.facts')}</h2>
                <dl>
                    <AgentRunMission code={run.mission_code} mission={mission} />
                    <Fact label={t('page.write_mode')}>{t(`page.write_mode_${run.write_mode}`)}</Fact>
                    <Fact label={t('page.params')}>
                        {params.length === 0 ? '—' : params.map(([k, v]) => `${k}: ${String(v)}`).join(', ')}
                    </Fact>
                    <Fact label={t('page.requested_by')}>{run.requested_by?.name ?? '—'}</Fact>
                    <Fact label={t('page.created')}>{date(run.created_at)}</Fact>
                    <Fact label={t('page.started')}>{date(run.started_at)}</Fact>
                    <Fact label={t('page.finished')}>{date(run.finished_at)}</Fact>
                    <Fact label={t('page.deadline')}>{date(run.deadline_at)}</Fact>
                    {internals && (
                        <>
                            <Fact label={t('page.claimed_by')}>{internals.claimed_by ?? '—'}</Fact>
                            <Fact label={t('page.harness_version')}>{internals.harness_version ?? '—'}</Fact>
                            <Fact label={t('page.bridge_version')}>{internals.bridge_version ?? '—'}</Fact>
                            <Fact label={t('page.mission_version')}>{run.mission_version || '—'}</Fact>
                            <Fact label={t('page.bundle_sha256')}><Hash value={internals.bundle_sha256} /></Fact>
                            <Fact label={t('page.prompt_sha256')}><Hash value={internals.prompt_sha256} /></Fact>
                        </>
                    )}
                </dl>
            </section>

            {internals && (
                <section className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="agent-run-usage">
                    <h2 className="text-base font-semibold text-foreground">{t('page.usage')}</h2>
                    <div className="grid gap-3 sm:grid-cols-3">
                        <Meter name="writes" value={internals.writes ?? 0} max={internals.max_writes ?? 0} />
                        <Meter name="api_calls" value={internals.api_calls ?? 0} max={internals.max_api_calls ?? 0} />
                        <Meter name="events" value={internals.events ?? 0} max={internals.max_events ?? 0} />
                    </div>
                    <p className="text-sm text-foreground">
                        {t('page.tokens', {
                            input: formatTokens(internals.input_tokens ?? 0, locale),
                            output: formatTokens(internals.output_tokens ?? 0, locale),
                        })}
                    </p>
                    <p className="text-sm text-foreground" data-testid="agent-run-cost">
                        {(internals.cost_usd ?? 0) > 0 || !(internals.list_cost_usd ?? 0)
                            ? t('page.spent', { cost: formatAICost(internals.cost_usd ?? 0, locale) })
                            : t('page.list_cost', { cost: formatAICost(internals.list_cost_usd ?? 0, locale) })}
                    </p>
                </section>
            )}

            {counts?.writes && Object.keys(counts.writes).length > 0 && (
                <section className="rounded-lg border border-border bg-card p-4" data-testid="agent-run-counts">
                    <h2 className="mb-2 text-base font-semibold text-foreground">{t('page.counts')}</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <tbody>
                                {Object.entries(counts.writes).map(([table, ops]) => (
                                    <tr key={table} className="border-t border-border first:border-t-0">
                                        <td className="py-1 pr-3 font-mono text-xs">{table}</td>
                                        <td className="py-1 text-muted-foreground">
                                            {Object.entries(ops).map(([op, n]) => `${op} ${n}`).join(' · ')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    )
}

function EventPayload({ org, event, mission }: { org: string; event: AgentRunEvent; mission?: AgentMission }) {
    const { t } = useTranslation('agent-runs')
    const p = event.payload as Record<string, unknown>
    switch (event.kind) {
        case 'message':
            return <p className="whitespace-pre-wrap text-foreground">{String(p.text ?? '')}</p>
        case 'step': {
            const step = mission?.steps.find((s) => s.code === p.code)
            const title = step ? t(step.title_key, { defaultValue: String(p.code) }) : String(p.code ?? '')
            return (
                <span className="text-foreground">
                    {title} · {t(`page.step_status.${String(p.status)}`, { defaultValue: String(p.status ?? '') })}
                    {p.done != null && p.total != null && ` · ${String(p.done)}/${String(p.total)}`}
                    {p.note ? ` · ${String(p.note)}` : ''}
                </span>
            )
        }
        case 'question':
            return (
                <span className="text-foreground">
                    {String(p.text ?? '')}
                    {p.choice_made ? <span className="text-muted-foreground"> → {String(p.choice_made)}</span> : null}
                </span>
            )
        case 'tool':
            return <span className="text-foreground">{String(p.name ?? '')} · {String(p.status ?? '')} · {String(p.ms ?? 0)} ms</span>
        case 'api_call':
            return <span className="font-mono text-xs text-foreground">{String(p.method)} {String(p.route)} → {String(p.status)} · {String(p.ms ?? 0)} ms</span>
        case 'cancel.requested':
            return (
                <span className="text-foreground">
                    {t('page.cancel_requested')}{' '}
                    {typeof p.by === 'string' ? (
                        <ActivityUserName org={org} userUid={p.by} fallback={t('page.someone')} />
                    ) : (
                        t(`page.cancel_reason.${String(p.reason ?? '')}`, { defaultValue: String(p.reason ?? '') })
                    )}
                </span>
            )
        default:
            return <pre className="whitespace-pre-wrap break-all font-mono text-xs text-muted-foreground">{JSON.stringify(p)}</pre>
    }
}

function eventQuery(chip: EventChip): { kind?: string; source?: 'server' } {
    if (chip === 'all') return {}
    if (chip === 'server') return { source: 'server' }
    return { kind: chip }
}

function EventsPage({
    org,
    runUid,
    chip,
    offset,
    mission,
    last,
    onMore,
    table,
}: {
    org: string
    runUid: string
    chip: EventChip
    offset: number
    mission?: AgentMission
    last: boolean
    onMore: () => void
    table?: boolean
}) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const { data, isLoading } = $api.useQuery('get', `${RUN_PATH}/events`, {
        params: { path: { orgCode: org, agentRunUid: runUid }, query: { ...eventQuery(chip), order: 'desc', limit: EVENTS_PAGE, offset } },
    })
    if (isLoading) return <Skeleton className="h-10 w-full" />
    const events = data?.data ?? []
    const time = (s: string) => new Date(s).toLocaleTimeString(locale)
    const more = last && data?.pagination.has_more

    if (table) {
        return (
            <>
                {events.map((e) => {
                    const p = e.payload as { method?: string; route?: string; status?: number; ms?: number }
                    const tint = (p.status ?? 0) >= 500 ? 'bg-destructive/10' : (p.status ?? 0) >= 400 ? 'bg-warning/10' : ''
                    return (
                        <tr key={e.uid} className={`border-t border-border ${tint}`} data-testid={`agent-run-api-call-${e.uid}`}>
                            <td className="py-1 pr-3 whitespace-nowrap tabular-nums">{time(e.created_at)}</td>
                            <td className="py-1 pr-3 font-mono text-xs">{p.method}</td>
                            <td className="py-1 pr-3 font-mono text-xs">{p.route}</td>
                            <td className="py-1 pr-3 tabular-nums">{p.status}</td>
                            <td className="py-1 tabular-nums text-right">{p.ms ?? 0}</td>
                        </tr>
                    )
                })}
                {more && (
                    <tr>
                        <td colSpan={5} className="py-2">
                            <Button variant="outline" size="sm" onClick={onMore} data-testid="agent-run-events-more">{t('page.load_more')}</Button>
                        </td>
                    </tr>
                )}
            </>
        )
    }

    return (
        <>
            {events.map((e) => (
                <li key={e.uid} className="grid gap-1 border-t border-border py-2 text-sm sm:grid-cols-[6rem_9rem_1fr]" data-testid={`agent-run-event-${e.uid}`}>
                    <span className="tabular-nums text-muted-foreground">{time(e.created_at)}</span>
                    <span className="text-muted-foreground">{t(`page.sources.${e.source}`)} · {e.kind}</span>
                    <div className="min-w-0"><EventPayload org={org} event={e} mission={mission} /></div>
                </li>
            ))}
            {more && (
                <li className="pt-2">
                    <Button variant="outline" size="sm" onClick={onMore} data-testid="agent-run-events-more">{t('page.load_more')}</Button>
                </li>
            )}
        </>
    )
}

function EventsTab({ org, runUid, mission }: { org: string; runUid: string; mission?: AgentMission }) {
    const { t } = useTranslation('agent-runs')
    const [chip, setChip] = useState<EventChip>('all')
    const [pages, setPages] = useState(1)
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
                {EVENT_CHIPS.map((c) => (
                    <Button
                        key={c}
                        size="sm"
                        variant={c === chip ? 'default' : 'outline'}
                        onClick={() => {
                            setChip(c)
                            setPages(1)
                        }}
                        data-testid={`agent-run-events-chip-${c}`}
                    >
                        {t(`page.chips.${c}`)}
                    </Button>
                ))}
            </div>
            <ul className="rounded-lg border border-border bg-card px-4 py-2" data-testid="agent-run-events">
                {Array.from({ length: pages }, (_, i) => (
                    <EventsPage
                        key={`${chip}-${i}`}
                        org={org}
                        runUid={runUid}
                        chip={chip}
                        offset={i * EVENTS_PAGE}
                        mission={mission}
                        last={i === pages - 1}
                        onMore={() => setPages((n) => n + 1)}
                    />
                ))}
            </ul>
        </div>
    )
}

function ApiCallsTab({ org, runUid }: { org: string; runUid: string }) {
    const { t } = useTranslation('agent-runs')
    const [pages, setPages] = useState(1)
    return (
        <div className="overflow-x-auto rounded-lg border border-border bg-card p-4" data-testid="agent-run-api-calls">
            <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                        <th className="py-1 pr-3 font-medium">{t('page.time')}</th>
                        <th className="py-1 pr-3 font-medium">{t('page.method')}</th>
                        <th className="py-1 pr-3 font-medium">{t('page.route')}</th>
                        <th className="py-1 pr-3 font-medium">{t('page.status')}</th>
                        <th className="py-1 font-medium text-right">ms</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: pages }, (_, i) => (
                        <EventsPage
                            key={i}
                            org={org}
                            runUid={runUid}
                            chip="api_call"
                            offset={i * EVENTS_PAGE}
                            last={i === pages - 1}
                            onMore={() => setPages((n) => n + 1)}
                            table
                        />
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function WritesTab({ org, runUid }: { org: string; runUid: string }) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/logs', {
        params: { path: { orgCode: org }, query: { agent_run_uid: runUid, limit: 200 } },
    })
    if (isLoading) return <Skeleton className="h-24 w-full" />
    const items = (data as { items?: unknown[] } | undefined)?.items ?? []
    return (
        <div data-testid="agent-run-writes">
            {items.length === 0 ? (
                <EmptyState variant="panel" title={t('page.writes_empty')} />
            ) : (
                <ul className="space-y-2">
                    {items.map((entry) => {
                        const e = entry as Parameters<typeof ActivityEntry>[0]['entry']
                        return <ActivityEntry key={e.uid} entry={e} org={org} locale={locale} linkEntity />
                    })}
                </ul>
            )}
        </div>
    )
}

function LlmPage({ org, runUid, after, last, onMore }: { org: string; runUid: string; after?: string; last: boolean; onMore: (a: string) => void }) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const { data, isLoading } = $api.useQuery('get', `${RUN_PATH}/llm-calls`, {
        params: { path: { orgCode: org, agentRunUid: runUid }, query: { order: 'desc', limit: LLM_PAGE, ...(after ? { after } : {}) } },
    })
    if (isLoading) return null
    const calls: AgentLlmCall[] = data?.data ?? []
    const cost = (c: AgentLlmCall) =>
        c.cost_usd != null
            ? formatAICost(c.cost_usd, locale)
            : c.list_cost_usd != null
                ? t('page.llm.list', { cost: formatAICost(c.list_cost_usd, locale) })
                : '—'
    const statusVariant = (s: string) => (s === 'ok' ? 'success' : s === 'pending' ? 'secondary' : s === 'limited' ? 'warning' : 'destructive')
    return (
        <>
            {calls.map((c) => (
                <tr key={c.uid} className="border-t border-border" data-testid={`agent-run-llm-call-${c.uid}`}>
                    <td className="py-1 pr-3 whitespace-nowrap tabular-nums">{new Date(c.created_at).toLocaleTimeString(locale)}</td>
                    <td className="py-1 pr-3">{c.provider}</td>
                    <td className="py-1 pr-3 font-mono text-xs">{c.model}</td>
                    <td className="py-1 pr-3"><Badge variant={statusVariant(c.status)}>{t(`page.llm.status.${c.status}`)}</Badge></td>
                    <td className="py-1 pr-3 text-right tabular-nums">{formatTokens(c.input_tokens, locale)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{formatTokens(c.output_tokens, locale)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{formatTokens(c.cache_read_tokens, locale)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{formatTokens(c.cache_write_tokens, locale)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{c.duration_ms != null ? `${c.duration_ms} ms` : '—'}</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{cost(c)}{c.estimated ? ` (${t('page.llm.estimated')})` : ''}</td>
                    <td className="py-1 pr-3 tabular-nums">{c.http_status ?? '—'}</td>
                    <td className="py-1 whitespace-nowrap">{c.limit_reset_at ? new Date(c.limit_reset_at).toLocaleString(locale) : '—'}</td>
                </tr>
            ))}
            {last && data?.next_after && (
                <tr>
                    <td colSpan={12} className="py-2">
                        <Button variant="outline" size="sm" onClick={() => onMore(data.next_after!)} data-testid="agent-run-llm-more">{t('page.load_more')}</Button>
                    </td>
                </tr>
            )}
            {!after && calls.length === 0 && (
                <tr>
                    <td colSpan={12} className="py-6 text-center text-muted-foreground" data-testid="agent-run-llm-empty">
                        {t('page.llm.empty')}
                    </td>
                </tr>
            )}
        </>
    )
}

function LlmCallsTab({ org, runUid }: { org: string; runUid: string }) {
    const { t } = useTranslation('agent-runs')
    const [afters, setAfters] = useState<(string | undefined)[]>([undefined])
    const cols = ['time', 'provider', 'model', 'status', 'input', 'output', 'cache_read', 'cache_write', 'duration', 'cost', 'http_status', 'limit_reset']
    return (
        <div className="overflow-x-auto rounded-lg border border-border bg-card p-4" data-testid="agent-run-llm-calls">
            <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                    <tr>{cols.map((c) => <th key={c} className="py-1 pr-3 font-medium whitespace-nowrap">{t(`page.llm.cols.${c}`)}</th>)}</tr>
                </thead>
                <tbody>
                    {afters.map((after, i) => (
                        <LlmPage key={after ?? 'first'} org={org} runUid={runUid} after={after} last={i === afters.length - 1} onMore={(a) => setAfters((xs) => [...xs, a])} />
                    ))}
                </tbody>
            </table>
        </div>
    )
}

/** The run page (spec 2026-10-21-19 §4). */
export function AgentRunPage({ org, runUid, tab }: { org: string; runUid: string; tab: AgentRunTab }) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const navigate = useNavigate()
    const { data: run, isLoading, refetch } = useAgentRun(org, runUid)
    const { data: missions } = useAgentMissions(org)
    const mission = missions?.data.find((m) => m.code === run?.mission_code)
    const target = useTargetName(org, run)
    const canDecide = useCan('data.write')
    const invalidate = useInvalidateAgentRuns(org)
    const [actionError, setActionError] = useState<string | undefined>()
    const [confirmCancel, setConfirmCancel] = useState(false)
    useAgentRunLive(org, run, refetch)

    const missionTitle = mission ? t(mission.title_key, { defaultValue: mission.code }) : run?.mission_code ?? ''
    useBreadcrumbs(
        useMemo(
            () => [
                { title: t('common:nav.home'), path: `/${org}` },
                ...(target.path ? [{ title: target.name ?? '…', path: target.path }] : []),
                { title: t('page.breadcrumb', { mission: missionTitle }) },
            ],
            [org, t, target.name, target.path, missionTitle],
        ),
    )

    if (isLoading) return <Skeleton className="h-40 w-full" data-testid="agent-run-page-loading" />
    if (!run) {
        return <EmptyState variant="panel" title={t('page.not_found')} data-testid="agent-run-page-not-found" />
    }

    const cfg = AGENT_RUN_STATUS[displayStatus(run)]
    const active = isActiveRun(run)
    const started = run.started_at ? new Date(run.started_at) : undefined
    const end = run.finished_at ? new Date(run.finished_at) : new Date()
    const description = [
        pairLabel(t, run.harness, run.model),
        started ? t('page.started_at', { date: started.toLocaleString(locale) }) : t('page.not_started'),
        started ? formatDuration(end.getTime() - started.getTime()) : null,
    ].filter(Boolean).join(' · ')

    const relaunch = async () => {
        setActionError(undefined)
        if (!run.target_entity_type || !run.target_entity_uid) return
        const { run: next, error } = await launchAgentRun(org, {
            mission: run.mission_code,
            target_entity_type: run.target_entity_type as TargetEntityType,
            target_entity_uid: run.target_entity_uid,
            harness: run.harness,
            model: run.model,
            params: run.params ?? {},
        })
        if (error || !next) {
            setActionError(launchErrorMessage(t, error ?? {}))
            return
        }
        await invalidate()
        navigate({ to: '/$org/agent-runs/$runUid', params: { org, runUid: next.uid } })
    }

    const action = (label: string, icon: string, testid: string, onClick: () => void) => (
        <Button variant="outline" size="sm" aria-label={label} title={label} onClick={onClick} data-testid={testid}>
            <AppIcon path={icon} size="sm" className="md:mr-1" />
            <span className="hidden md:inline">{label}</span>
        </Button>
    )

    return (
        <div className="space-y-6" data-testid="agent-run-page">
            <PageHeader
                icon={SECTION_ICONS.agentRun}
                title={target.name ? `${missionTitle} · ${target.name}` : missionTitle}
                titleAside={
                    <Badge variant={toneVariant(cfg.tone)} className="gap-1" data-testid="agent-run-status">
                        <AppIcon path={cfg.icon} size="xs" />
                        {t(cfg.labelKey)}
                    </Badge>
                }
                description={description}
                actions={
                    <>
                        {canDecide && action(t('page.relaunch'), mdiRefresh, 'agent-run-relaunch', () => void relaunch())}
                        {run.internals?.has_transcript &&
                            action(t('page.transcript'), mdiDownload, 'agent-run-transcript', () => {
                                downloadAgentRunTranscript(org, run.uid).catch(() => setActionError(t('page.transcript_failed')))
                            })}
                        {canDecide && active && action(t('card.cancel'), mdiStopCircleOutline, 'agent-run-cancel', () => setConfirmCancel(true))}
                    </>
                }
            />

            {actionError && (
                <div className="rounded-md border border-destructive/25 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="agent-run-action-error">
                    {actionError}
                </div>
            )}

            <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-border">
                {AGENT_RUN_TABS.map((key) => (
                    <button
                        key={key}
                        role="tab"
                        aria-selected={tab === key}
                        className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm ${tab === key ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        onClick={() => navigate({ to: '/$org/agent-runs/$runUid', params: { org, runUid }, search: key === 'summary' ? {} : { tab: key }, replace: true })}
                        data-testid={`agent-run-tab-${key}`}
                    >
                        {t(`page.tabs.${key}`)}
                    </button>
                ))}
            </div>

            {tab === 'summary' && <SummaryTab org={org} run={run} mission={mission} />}
            {tab === 'events' && <EventsTab org={org} runUid={run.uid} mission={mission} />}
            {tab === 'api-calls' && <ApiCallsTab org={org} runUid={run.uid} />}
            {tab === 'writes' && <WritesTab org={org} runUid={run.uid} />}
            {tab === 'llm-calls' && <LlmCallsTab org={org} runUid={run.uid} />}

            <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
                <AlertDialogContent data-testid="agent-run-decision-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('card.cancel')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('card.confirm_cancel')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="agent-run-decision-dismiss">{t('card.keep')}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="agent-run-decision-submit"
                            onClick={async () => {
                                const err = await runAction(org, run.uid, 'cancel')
                                if (err) setActionError(err.title || t('card.decision_failed'))
                                await invalidate()
                            }}
                        >
                            {t('card.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
